import { useMemo } from "react"

import { ProjectPermissions } from "shared/types/__generated__/graphql"

import { ProjectPermissionType } from "entities/projects/types/ProjectPermissions"
import { TeamRole } from "entities/team-members/types/TeamRole"

import { ProjectScopedActionHandlers } from "../types/ProjectActionHandlers"
import {
  UseProjectActionHandlersArgs,
  useProjectActionHandlers
} from "./useProjectActionHandlers"

interface UseProjectScopedActionHandlersArgs
  extends UseProjectActionHandlersArgs {
  projectID: string
  permissions: ProjectPermissions
  isTeamProject: boolean
  role: TeamRole | null | undefined
}

export type FilteredProjectScopedActionHandlers = Omit<
  ProjectScopedActionHandlers,
  | "onEdit"
  | "onAddClarificationRequest"
  | "onDelete"
  | "onShare"
  | "onManageTeam"
  | "onInviteMember"
  | "onLeaveTeam"
  | "onDuplicate"
> & {
  onEdit: (() => void) | undefined
  onAddClarificationRequest: (() => void) | undefined
  onDelete: (() => void) | undefined
  onShare: (() => void) | undefined
  onManageTeam: (() => void) | undefined
  onInviteMember: (() => void) | undefined
  onLeaveTeam: (() => void) | undefined
  onDuplicate: (() => void) | undefined
}

export function useProjectScopedActionHandlers({
  projectID,
  onAction,
  permissions,
  isTeamProject,
  role
}: UseProjectScopedActionHandlersArgs): FilteredProjectScopedActionHandlers {
  const handlers = useProjectActionHandlers({ onAction })
  const scoped = useMemo(
    () =>
      // Object.fromEntries/entries strips the type; the cast is safe because
      // every key in handlers maps to the same key in ProjectScopedActionHandlers
      // with projectID partially applied as the first argument.
      Object.fromEntries(
        Object.entries(handlers).map(([key, fn]) => [
          key,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (...args: any) => fn(projectID, ...args)
        ])
      ) as unknown as ProjectScopedActionHandlers,
    [projectID, handlers]
  )

  return useMemo(
    () => ({
      ...scoped,
      onEdit: permissions[ProjectPermissionType.EDIT]
        ? scoped.onEdit ?? undefined
        : undefined,
      onAddClarificationRequest: permissions[ProjectPermissionType.EDIT]
        ? scoped.onAddClarificationRequest ?? undefined
        : undefined,
      onDelete: permissions[ProjectPermissionType.DELETE]
        ? scoped.onDelete
        : undefined,
      onShare: isTeamProject ? undefined : scoped.onShare,
      onManageTeam:
        isTeamProject && role === TeamRole.ADMIN
          ? scoped.onManageTeam
          : undefined,
      onInviteMember: isTeamProject ? undefined : scoped.onInviteMember,
      onLeaveTeam: isTeamProject ? scoped.onLeaveTeam : undefined,
      onDuplicate: isTeamProject ? undefined : scoped.onDuplicate
    }),
    [scoped, permissions, isTeamProject, role]
  )
}
